import type { Criterion } from '@/features/criteria/types';

// =========================================
// Pre-defined criteria templates
// =========================================
// Stored client-side to avoid DB seed/FK issues.
// Users clone templates into their own criteria via useCloneTemplate.

const SYSTEM_USER = '00000000-0000-0000-0000-000000000000';
const now = new Date().toISOString();

function tpl(
  id: string,
  overrides: Omit<Criterion, 'id' | 'user_id' | 'is_template' | 'created_at' | 'updated_at'>
): Criterion {
  return {
    id,
    user_id: SYSTEM_USER,
    is_template: true,
    created_at: now,
    updated_at: now,
    ...overrides,
  };
}

export const CRITERIA_TEMPLATES: Criterion[] = [
  // ---- Boolean templates ----
  tpl('tpl-bool-peer-reviewed', {
    name: 'Peer Reviewed',
    description: 'Is the source published in a peer-reviewed journal?',
    type: 'boolean',
    config: { question: 'Is this article published in a peer-reviewed journal?' },
    weight: 3,
    category: 'Quality',
  }),
  tpl('tpl-bool-rct', {
    name: 'Randomized Controlled Trial',
    description: 'Is this a randomized controlled trial (RCT)?',
    type: 'boolean',
    config: { question: 'Is this study a randomized controlled trial?' },
    weight: 4,
    category: 'Study Design',
  }),
  tpl('tpl-bool-ethics', {
    name: 'Ethics Approval',
    description: 'Does the study report ethics committee approval?',
    type: 'boolean',
    config: { question: 'Does the study mention ethics committee or IRB approval?' },
    weight: 2,
    category: 'Quality',
  }),

  // ---- Scale templates ----
  tpl('tpl-scale-sample-size', {
    name: 'Sample Size Adequacy',
    description: 'Rate the adequacy of the sample size for the study conclusions',
    type: 'scale',
    config: {
      min: 1,
      max: 10,
      step: 1,
      labels: { '1': 'Very inadequate', '5': 'Adequate', '10': 'Excellent' },
    },
    weight: 3,
    category: 'Methodology',
  }),
  tpl('tpl-scale-rigor', {
    name: 'Methodological Rigor',
    description: 'Rate the overall methodological quality',
    type: 'scale',
    config: {
      min: 1,
      max: 5,
      step: 1,
      labels: { '1': 'Very poor', '3': 'Acceptable', '5': 'Excellent' },
    },
    weight: 4,
    category: 'Methodology',
  }),
  tpl('tpl-scale-relevance', {
    name: 'Relevance to Topic',
    description: 'How relevant is this source to the research question?',
    type: 'scale',
    config: {
      min: 1,
      max: 5,
      step: 1,
      labels: { '1': 'Not relevant', '3': 'Somewhat relevant', '5': 'Highly relevant' },
    },
    weight: 5,
    category: 'Relevance',
  }),

  // ---- Text Match templates ----
  tpl('tpl-text-stats', {
    name: 'Statistical Methods',
    description: 'Check for presence of key statistical methods',
    type: 'text_match',
    config: {
      keywords: [
        'regression',
        'ANOVA',
        't-test',
        'chi-square',
        'confidence interval',
        'p-value',
        'effect size',
      ],
      mode: 'any',
      case_sensitive: false,
    },
    weight: 3,
    category: 'Methodology',
  }),
  tpl('tpl-text-limitations', {
    name: 'Study Limitations',
    description: 'Check if the study discusses its limitations',
    type: 'text_match',
    config: {
      keywords: ['limitation', 'limitations', 'weakness', 'shortcoming', 'caveat'],
      mode: 'any',
      case_sensitive: false,
    },
    weight: 2,
    category: 'Quality',
  }),

  // ---- PICO templates ----
  tpl('tpl-pico-generic', {
    name: 'PICO Framework',
    description: 'Evaluate against PICO criteria',
    type: 'pico',
    config: {
      population: '',
      intervention: '',
      comparator: '',
      outcome: '',
      instructions:
        'Evaluate how well this source matches each PICO component. Fill in specific values before running.',
    },
    weight: 5,
    category: 'Framework',
  }),
  tpl('tpl-pico-clinical', {
    name: 'Clinical Trial PICO',
    description: 'PICO evaluation for clinical trials',
    type: 'pico',
    config: {
      population: 'Adult patients',
      intervention: 'Drug or treatment under study',
      comparator: 'Placebo or standard of care',
      outcome: 'Primary clinical endpoint',
      instructions:
        'Score each PICO component based on how well the study defines and reports it.',
    },
    weight: 5,
    category: 'Framework',
  }),

  // ---- Bias Risk templates ----
  tpl('tpl-bias-rob2', {
    name: 'Risk of Bias (RoB 2)',
    description: 'Cochrane Risk of Bias assessment for randomized trials',
    type: 'bias_risk',
    config: {
      checklist: [
        'Randomization process',
        'Deviations from intended interventions',
        'Missing outcome data',
        'Measurement of the outcome',
        'Selection of the reported result',
      ],
      scale: ['low', 'some_concerns', 'high'],
    },
    weight: 4,
    category: 'Bias',
  }),
  tpl('tpl-bias-robins', {
    name: 'ROBINS-I',
    description: 'Risk of bias in non-randomized studies of interventions',
    type: 'bias_risk',
    config: {
      checklist: [
        'Confounding',
        'Selection of participants',
        'Classification of interventions',
        'Deviations from intended interventions',
        'Missing data',
        'Measurement of outcomes',
        'Selection of reported result',
      ],
      scale: ['low', 'moderate', 'serious', 'critical'],
    },
    weight: 4,
    category: 'Bias',
  }),

  // ---- CASP templates ----
  tpl('tpl-casp-rct', {
    name: 'CASP RCT',
    description: 'CASP Checklist for Randomised Controlled Trials',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clearly focused question',
        'Random assignment',
        'Participants properly accounted for',
        'Participants/investigators blinded',
        'Groups similar at start',
        'Groups treated equally',
        'How large was the treatment effect',
        'How precise was the estimate',
        'Can results be applied locally',
        'Were all important outcomes considered',
        'Benefits worth the harms and costs',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),
  tpl('tpl-casp-sr', {
    name: 'CASP Systematic Review',
    description: 'CASP Checklist for Systematic Reviews',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clearly focused question',
        'Right type of papers included',
        'Important studies included',
        'Quality of studies assessed',
        'Reasonable to combine results',
        'Overall results presented',
        'Results precise',
        'Can results be applied locally',
        'All important outcomes considered',
        'Benefits worth harms and costs',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),
  tpl('tpl-casp-cohort', {
    name: 'CASP Cohort',
    description: 'CASP Checklist for Cohort Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clearly focused issue',
        'Cohort recruited acceptably',
        'Exposure accurately measured',
        'Outcome accurately measured',
        'Important confounders identified',
        'Follow-up complete enough',
        'Results of the study',
        'Results precise',
        'Results believable',
        'Can results be applied locally',
        'Results fit with other evidence',
        'Implications for practice',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),
  tpl('tpl-casp-cc', {
    name: 'CASP Case-Control',
    description: 'CASP Checklist for Case-Control Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clearly focused issue',
        'Appropriate method used',
        'Cases recruited acceptably',
        'Controls selected acceptably',
        'Exposure accurately measured',
        'Confounding factors identified',
        'Results of the study',
        'Results precise',
        'Results believable',
        'Can results be applied locally',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),
  tpl('tpl-casp-qual', {
    name: 'CASP Qualitative',
    description: 'CASP Checklist for Qualitative Research',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clear statement of aims',
        'Qualitative methodology appropriate',
        'Research design appropriate',
        'Recruitment strategy appropriate',
        'Data collected appropriately',
        'Researcher-participant relationship considered',
        'Ethical issues considered',
        'Data analysis rigorous',
        'Clear statement of findings',
        'Value of the research',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),
  tpl('tpl-casp-diag', {
    name: 'CASP Diagnostic',
    description: 'CASP Checklist for Diagnostic Test Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Recognizable patient group',
        'Good reference standard used',
        'All patients got diagnostic and reference test',
        'Could results have been influenced by reference standard',
        'Disease status clearly described',
        'Adequate description of test methods',
        'Sensible cut-off values used',
        'Results repeatable',
        'Results applicable in clinical setting',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),
  tpl('tpl-casp-econ', {
    name: 'CASP Economic',
    description: 'CASP Checklist for Economic Evaluations',
    type: 'bias_risk',
    config: {
      checklist: [
        'Well-defined question',
        'Comprehensive description of alternatives',
        'Effectiveness established',
        'Effects of interventions identified',
        'Effects measured and valued appropriately',
        'Costs identified',
        'Costs measured and valued appropriately',
        'Discounting applied',
        'Sensitivity analysis performed',
        'Results applicable locally',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 3,
    category: 'CASP',
  }),
  tpl('tpl-casp-xs', {
    name: 'CASP Cross-Sectional',
    description: 'CASP Checklist for Cross-Sectional Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clearly focused issue',
        'Appropriate method used',
        'Subjects recruited acceptably',
        'Exposure accurately measured',
        'Outcome accurately measured',
        'Confounders identified and controlled',
        'Results presented clearly',
        'Results precise',
        'Results believable',
      ],
      scale: ['yes', 'no', "can't tell"],
    },
    weight: 4,
    category: 'CASP',
  }),

  // ---- JBI templates ----
  tpl('tpl-jbi-rct', {
    name: 'JBI RCTs',
    description: 'JBI Critical Appraisal Checklist for Randomized Controlled Trials',
    type: 'bias_risk',
    config: {
      checklist: [
        'True randomization',
        'Allocation concealment',
        'Treatment groups similar at baseline',
        'Participants blinded',
        'Outcome assessors blinded',
        'Treatment groups treated identically',
        'Follow-up complete or differences described',
        'Participants analyzed in assigned groups',
        'Outcomes measured the same way',
        'Outcomes measured reliably',
        'Appropriate statistical analysis',
        'Trial design appropriate',
        'Deviations from protocol addressed',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-quasi', {
    name: 'JBI Quasi-Experimental',
    description: 'JBI Checklist for Quasi-Experimental Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clear cause-effect relationship',
        'Participants similar in comparison groups',
        'Participants receiving similar treatment',
        'Control group present',
        'Multiple measurements pre/post',
        'Follow-up complete or described',
        'Outcomes measured the same way',
        'Outcomes measured reliably',
        'Appropriate statistical analysis',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-cohort', {
    name: 'JBI Cohort',
    description: 'JBI Checklist for Cohort Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Groups similar and recruited from same population',
        'Exposures measured similarly',
        'Exposure measured validly and reliably',
        'Confounding factors identified',
        'Strategies to deal with confounders stated',
        'Groups/participants free of outcome at start',
        'Outcomes measured validly and reliably',
        'Follow-up time sufficient',
        'Follow-up complete or reasons described',
        'Strategies to address incomplete follow-up',
        'Appropriate statistical analysis',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-cc', {
    name: 'JBI Case-Control',
    description: 'JBI Checklist for Case-Control Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Groups comparable other than exposure',
        'Cases and controls matched appropriately',
        'Same criteria for case identification',
        'Exposure measured in standard, valid and reliable way',
        'Exposure measured the same way for cases and controls',
        'Confounding factors identified',
        'Strategies to deal with confounders stated',
        'Outcomes assessed in standard, valid and reliable way',
        'Exposure period sufficient',
        'Appropriate statistical analysis',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-xs', {
    name: 'JBI Cross-Sectional',
    description: 'JBI Checklist for Analytical Cross-Sectional Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Inclusion criteria clearly defined',
        'Study subjects and setting described in detail',
        'Exposure measured validly and reliably',
        'Objective standard criteria used',
        'Confounding factors identified',
        'Strategies to deal with confounders stated',
        'Outcomes measured validly and reliably',
        'Appropriate statistical analysis',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-case-report', {
    name: 'JBI Case Reports',
    description: 'JBI Checklist for Case Reports',
    type: 'bias_risk',
    config: {
      checklist: [
        'Patient demographics clearly described',
        'Patient history clearly described',
        'Current clinical condition clearly described',
        'Diagnostic tests clearly described',
        'Intervention clearly described',
        'Post-intervention clinical condition described',
        'Adverse events identified and described',
        'Takeaway lessons clearly described',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 3,
    category: 'JBI',
  }),
  tpl('tpl-jbi-case-series', {
    name: 'JBI Case Series',
    description: 'JBI Checklist for Case Series',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clear criteria for inclusion',
        'Condition measured in standard, reliable way',
        'Valid methods for condition identification',
        'Consecutive and complete inclusion',
        'Clear reporting of demographics',
        'Clear reporting of clinical information',
        'Outcomes clearly reported',
        'Reporting of presenting site demographics',
        'Appropriate statistical analysis',
        'Appropriate sub-populations identified',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 3,
    category: 'JBI',
  }),
  tpl('tpl-jbi-qual', {
    name: 'JBI Qualitative',
    description: 'JBI Checklist for Qualitative Research',
    type: 'bias_risk',
    config: {
      checklist: [
        'Congruity between philosophical perspective and methodology',
        'Congruity between methodology and research question',
        'Congruity between methodology and data collection',
        'Congruity between methodology and data analysis',
        'Congruity between methodology and interpretation',
        'Researcher culturally or theoretically located',
        'Influence of researcher addressed',
        'Participants and voices adequately represented',
        'Ethical approval obtained',
        'Conclusions flow from analysis',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-sr', {
    name: 'JBI Systematic Reviews',
    description: 'JBI Checklist for Systematic Reviews',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clear review question',
        'Appropriate inclusion criteria',
        'Appropriate search strategy',
        'Adequate sources and resources used',
        'Criteria for appraising studies appropriate',
        'Critical appraisal by two or more reviewers',
        'Methods to minimize extraction errors',
        'Results combined appropriately',
        'Likelihood of publication bias assessed',
        'Recommendations supported by data',
        'Specific directives for new research',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-text', {
    name: 'JBI Text & Opinion',
    description: 'JBI Checklist for Text and Opinion Papers',
    type: 'bias_risk',
    config: {
      checklist: [
        'Source of opinion identified',
        'Source has standing in field',
        'Interests of target population central',
        'Logical argument/discussion present',
        'Reference to extant literature',
        'Incongruence with literature addressed',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 3,
    category: 'JBI',
  }),
  tpl('tpl-jbi-prevalence', {
    name: 'JBI Prevalence',
    description: 'JBI Checklist for Studies Reporting Prevalence Data',
    type: 'bias_risk',
    config: {
      checklist: [
        'Sample frame appropriate',
        'Participants sampled appropriately',
        'Adequate sample size',
        'Study subjects and setting described',
        'Data analysis conducted with sufficient coverage',
        'Valid methods for condition identification',
        'Condition measured in standard, reliable way',
        'Appropriate statistical analysis',
        'Response rate adequate',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-diag', {
    name: 'JBI Diagnostic',
    description: 'JBI Checklist for Diagnostic Test Accuracy Studies',
    type: 'bias_risk',
    config: {
      checklist: [
        'Representative sample of patients',
        'Index test clearly described',
        'Reference standard likely to classify condition',
        'Patients received both tests',
        'Same reference standard for all patients',
        'Tests independent of each other',
        'Reference standard interpreted without knowledge of index test',
        'Index test interpreted without knowledge of reference standard',
        'Same clinical data available as in practice',
        'Uninterpretable results reported',
        'Withdrawals explained',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 4,
    category: 'JBI',
  }),
  tpl('tpl-jbi-econ', {
    name: 'JBI Economic',
    description: 'JBI Checklist for Economic Evaluations',
    type: 'bias_risk',
    config: {
      checklist: [
        'Clear question in answerable form',
        'Description of alternatives comprehensive',
        'Relevant costs and outcomes identified',
        'Clinical effectiveness established',
        'Costs and outcomes measured accurately',
        'Costs and outcomes valued credibly',
        'Costs and outcomes adjusted for timing',
        'Incremental analysis of costs and consequences',
        'Sensitivity analyses performed',
        'Study results include all relevant concerns',
        'Results generalizable to local setting',
      ],
      scale: ['yes', 'no', 'unclear', 'not_applicable'],
    },
    weight: 3,
    category: 'JBI',
  }),
];
